import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { lazy, Suspense, useMemo } from 'react';
import cn from 'classnames';
import { Box, Typography } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { useLocaleKeys } from '~/i18n';
import { MetricsCard } from '../MetricsCard/MetricsCard';
import styles from './SparklineCard.module.scss';
const SPARKLINE_WIDTH = 72;
const SPARKLINE_HEIGHT = 24;
const SparklineChart = /*#__PURE__*/ lazy(()=>import(/* webpackChunkName: "recharts" */ './SparklineChartInner').then((module)=>({
            default: module.SparklineChartInner
        })));
export const GetSparklineCardTID = (parentTestId)=>getTestIds(parentTestId);
export const SparklineCard = (param)=>{
    let { title, tooltip, primaryValue, sparkline, trendPercent, fallbackSubtitle, isEmpty, emptyPrimary, emptySubtitle, isActive = false, isEditMode = false, isLoading = false, isRefetching = false, onRemove, dragHandle, onClick, testId } = param;
    const { SparklineCardTestIds } = useMemo(()=>GetSparklineCardTID(testId), [
        testId
    ]);
    const { t: invoiceMetricsSectionTexts } = useLocaleKeys('components.invoiceMetricsSection');
    const trendIsUp = trendPercent != null && trendPercent > 0;
    const trendIsDown = trendPercent != null && trendPercent < 0;
    const trendSubtitle = trendIsUp ? invoiceMetricsSectionTexts.trend.up({
        percent: Math.round(trendPercent !== null && trendPercent !== void 0 ? trendPercent : 0)
    }) : trendIsDown ? invoiceMetricsSectionTexts.trend.down({
        percent: Math.abs(Math.round(trendPercent !== null && trendPercent !== void 0 ? trendPercent : 0))
    }) : fallbackSubtitle;
    const subtitleText = isEmpty ? emptySubtitle : trendSubtitle;
    const primaryText = isEmpty ? emptyPrimary : primaryValue;
    const showSparkline = !isEmpty && sparkline.length >= 2;
    const chartData = useMemo(()=>sparkline.map((value, index)=>({
                index,
                value
            })), [
        sparkline
    ]);
    return /*#__PURE__*/ _jsx(MetricsCard, {
        title: title,
        tooltip: tooltip,
        isActive: isActive,
        isDisabled: isEmpty,
        isEditMode: isEditMode,
        isLoading: isLoading,
        isRefetching: isRefetching,
        onRemove: onRemove,
        dragHandle: dragHandle,
        onClick: onClick,
        testId: SparklineCardTestIds.Root(),
        children: /*#__PURE__*/ _jsxs(Box, {
            className: styles.body,
            children: [
                /*#__PURE__*/ _jsxs(Box, {
                    className: styles.valueRow,
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "h5",
                            weight: "normal",
                            testId: SparklineCardTestIds.PrimaryValue(),
                            className: styles.primaryValue,
                            children: primaryText
                        }),
                        showSparkline && /*#__PURE__*/ _jsx("div", {
                            "data-testid": SparklineCardTestIds.Sparkline(),
                            className: styles.sparkline,
                            style: {
                                width: SPARKLINE_WIDTH,
                                height: SPARKLINE_HEIGHT
                            },
                            children: /*#__PURE__*/ _jsx(Suspense, {
                                fallback: null,
                                children: /*#__PURE__*/ _jsx(SparklineChart, {
                                    data: chartData
                                })
                            })
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(Typography, {
                    variant: "paragraph3",
                    skin: "secondary",
                    testId: SparklineCardTestIds.Subtitle(),
                    className: cn(styles.subtitle, {
                        [styles.subtitleUp]: !isEmpty && trendIsUp,
                        [styles.subtitleDown]: !isEmpty && trendIsDown
                    }),
                    children: subtitleText
                })
            ]
        })
    });
};
